/**
 * Rapid-fire block helpers: pure, so SurveySession carries only the wiring. The clock is anchored
 * SERVER-side (`ends_at`), so a reload resumes the real time and pausing the tab buys none.
 */
import type { SurveyBlockDefinition } from '../../utils/surveyApi';
import { VOICE_CAPTURE } from '../../config/constants';

/** Mirrors `RAPID_FIRE_CONFIG['ALLOWED_QUESTION_TYPES']` in `constants/interview_constants.py`.
 *  short_text/long_text are answered by SPEECH here, not typing — see ADR-0002 §1. */
export const RAPID_FIRE_ALLOWED_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'yes_no',
  'rating_scale',
  'likert_scale',
  'nps',
  'dropdown',
  'numeric_input',
  'short_text',
  'long_text',
]);

export const RAPID_FIRE_DEFAULT_DURATION_SECONDS = 60;

/** The seconds-remaining threshold where the countdown switches to its urgent treatment. */
export const RAPID_FIRE_URGENT_SECONDS = 10;

export interface RapidFireConfig {
  enabled: boolean;
  duration_seconds: number;
  max_questions?: number | null;
  allow_skip: boolean;
  /** How long a spoken answer may go quiet before advancing, in SECONDS. Per block, because the
   *  right pause is a property of the questions; absent = the 2s default. */
  silence_seconds?: number;
  intro_text?: string;
}

/** Derived from the constant rather than restated, so the two cannot say different things. */
export const RAPID_FIRE_DEFAULT_SILENCE_SECONDS = VOICE_CAPTURE.SILENCE_ADVANCE_MS / 1000;

/**
 * The silence window this block runs on, in ms. Fails OPEN toward the default for anything that
 * cannot be a window: zero would advance the instant the respondent drew breath.
 */
export function silenceAdvanceMsFor(config: RapidFireConfig | null | undefined): number {
  const seconds = config?.silence_seconds;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return VOICE_CAPTURE.SILENCE_ADVANCE_MS;
  }
  return Math.round(seconds * 1000);
}

export type RapidFireCloseReason = 'expired' | 'cap';

interface QuestionLike {
  id: string;
  type?: string;
  block_id?: string | null;
}

interface ResponseLike {
  question_id: string;
  state?: string;
}

/** The block's rapid-fire config, or null when that block is an ordinary one. */
export function rapidFireConfigFor(
  blocks: SurveyBlockDefinition[] | null | undefined,
  blockId: string | null | undefined,
): RapidFireConfig | null {
  if (!blocks || !blockId) return null;
  const block = blocks.find((b) => String(b.id) === String(blockId));
  const cfg = (block as { rapid_fire?: RapidFireConfig } | undefined)?.rapid_fire;
  return cfg && cfg.enabled ? cfg : null;
}

export function isRapidFireBlock(
  blocks: SurveyBlockDefinition[] | null | undefined,
  blockId: string | null | undefined,
): boolean {
  return rapidFireConfigFor(blocks, blockId) !== null;
}

/** Where the block sits in DISPLAY order, not array index — under randomization the two differ.
 *  Null when no question belongs to the block. */
export function blockDisplayRange(
  questions: QuestionLike[],
  questionOrder: number[] | null | undefined,
  blockId: string,
): { start: number; end: number; size: number } | null {
  const order = questionOrder?.length ? questionOrder : questions.map((_, i) => i);
  const positions: number[] = [];
  order.forEach((qIdx, pos) => {
    const q = questions[qIdx];
    if (q && String(q.block_id ?? '') === String(blockId)) positions.push(pos);
  });
  if (!positions.length) return null;
  return { start: positions[0], end: positions[positions.length - 1], size: positions.length };
}

/** Seconds left on the server deadline. Rounds UP so 0 is never shown while they can still
 *  answer; null when there is no usable deadline, which callers read as "keep going". */
export function remainingSeconds(
  endsAt: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - nowMs) / 1000));
}

/** How many questions in this block the respondent has actually answered (a skip doesn't count). */
export function answeredCountInBlock(
  questions: QuestionLike[],
  blockId: string,
  responses: ResponseLike[] | null | undefined,
): number {
  if (!responses?.length) return 0;
  const inBlock = new Set(
    questions.filter((q) => String(q.block_id ?? '') === String(blockId)).map((q) => String(q.id)),
  );
  return responses.filter((r) => r.state === 'answered' && inBlock.has(String(r.question_id)))
    .length;
}

/**
 * How many of the block's questions went unanswered: the union of `not_reached_question_ids`
 * (never served) and `expired_question_id` (reached, caught by the clock). A skip is in neither.
 */
export function unansweredAtCloseCount(input: {
  notReachedQuestionIds: readonly string[] | null | undefined;
  expiredQuestionId: string | null | undefined;
  blockSize: number;
  answeredInBlock: number;
}): number {
  const { notReachedQuestionIds, expiredQuestionId, blockSize, answeredInBlock } = input;

  // No list at all (an older server, or a field that failed to serialize): fall back to the
  // subtraction. It over-counts a skipped block, which is why it is never used when the server
  // did answer — but reporting a confident 0 would be the worse lie.
  if (!notReachedQuestionIds) return Math.max(0, blockSize - answeredInBlock);

  // A set, not a length + 1: the two server fields are disjoint today, and a union keeps this
  // honest if that ever stops being true rather than double-counting one question.
  const outstanding = new Set(notReachedQuestionIds.map(String));
  if (expiredQuestionId) outstanding.add(String(expiredQuestionId));
  return outstanding.size;
}

/**
 * Whether the block is over, and why. Expiry wins a tie: the respondent was still looking at a
 * question, so "ran out of time" is the honest record.
 */
export function shouldCloseRapidFire(input: {
  remaining: number | null;
  answeredInBlock: number;
  blockSize: number;
  maxQuestions?: number | null;
}): { close: boolean; reason: RapidFireCloseReason | null } {
  const { remaining, answeredInBlock, blockSize, maxQuestions } = input;

  if (remaining !== null && remaining <= 0) return { close: true, reason: 'expired' };

  // A cap of null/0/negative means "no cap" — fail OPEN toward showing questions, never toward a
  // block that closes the instant it opens. Authoring rejects those values anyway; this is the
  // runtime's second line of defence for data already in the wild.
  const cap =
    typeof maxQuestions === 'number' && maxQuestions > 0
      ? Math.min(maxQuestions, blockSize)
      : blockSize;

  if (answeredInBlock >= cap) return { close: true, reason: 'cap' };
  return { close: false, reason: null };
}

/** How long past its deadline a round may run so the last question can be answered — ADR-0006 §3. */
export const RAPID_FIRE_FINISH_ANSWER_GRACE_MS = 30_000;

/** The one answer of overrun a round has granted, or null before the buzzer has gone. */
export interface RapidFireOverrun {
  /** The question they were answering when the clock ran out. */
  questionId: string | null;
  expiresAtMs: number;
}

/**
 * Must the round wait, because the question on screen is unanswered? The deadline marks the LAST
 * question rather than taking it away — ADR-0006 §2. The returned latch bounds the overrun to one.
 */
export function shouldFinishTheQuestionOnScreen(input: {
  nowMs: number;
  overrun: RapidFireOverrun | null;
  currentQuestionId: string | null;
  /** A spoken answer already advanced whose transcript has not been patched in yet. */
  transcriptOutstanding: boolean;
}): { wait: boolean; overrun: RapidFireOverrun | null } {
  const { nowMs, overrun, currentQuestionId, transcriptOutstanding } = input;

  if (!overrun) {
    // The buzzer's first tick: whatever is on screen is the round's last question.
    return {
      wait: true,
      overrun: {
        questionId: currentQuestionId,
        expiresAtMs: nowMs + RAPID_FIRE_FINISH_ANSWER_GRACE_MS,
      },
    };
  }

  // The bound, checked first so nothing below can talk its way past it.
  if (nowMs >= overrun.expiresAtMs) return { wait: false, overrun };

  // "They finished" and "we have it" are two different moments for a spoken answer — ADR-0006 §3.
  if (transcriptOutstanding) return { wait: true, overrun };

  // Their answer landed and the runtime moved on. EXACTLY one question of overrun — carrying the
  // wait onto the next question would make the buzzer advisory.
  if (currentQuestionId !== overrun.questionId) return { wait: false, overrun };

  // Still on the question the buzzer caught, so it still has an answer owed to it.
  return { wait: true, overrun };
}

/** The intro copy for a block, with the duration filled in. */
export function rapidFireIntroText(config: RapidFireConfig): string {
  const seconds = config.duration_seconds ?? RAPID_FIRE_DEFAULT_DURATION_SECONDS;
  if (config.intro_text?.trim()) return config.intro_text;
  return (
    `You'll have ${seconds} seconds to answer as many questions as you can. ` +
    'Answer quickly — there are no right or wrong answers.'
  );
}

/** Submit from the answer itself: allowed types minus the typed one and the spoken ones. */
/** Answered by SPEECH: advances on the silence window rather than on a tap. */
export const RAPID_FIRE_SPOKEN_TYPES: ReadonlySet<string> = new Set(['short_text', 'long_text']);

/** Submit from the answer itself: allowed, minus the typed one and minus the spoken ones. */
export const RAPID_FIRE_TAP_ANSWER_TYPES: ReadonlySet<string> = new Set(
  [...RAPID_FIRE_ALLOWED_TYPES].filter(
    (t) => t !== 'numeric_input' && !RAPID_FIRE_SPOKEN_TYPES.has(t),
  ),
);

export interface RapidFireRule {
  id: string;
  text: string;
  emphasis: 'rule' | 'reassurance';
}

/**
 * What this round will actually do, derived from the block — author prose goes stale silently, and
 * a card that promises what the runtime refuses is worse than no card (ADR-0004).
 */
export function rapidFireRules(
  config: RapidFireConfig,
  blockQuestionTypes: string[],
): RapidFireRule[] {
  const rules: RapidFireRule[] = [];
  const count = blockQuestionTypes.length;
  // The cap ends the block early, so it — not the block's size — is what the respondent will meet.
  const cap =
    typeof config.max_questions === 'number' && config.max_questions > 0
      ? Math.min(config.max_questions, count)
      : count;

  // The cap bounds ANSWERS and the block bounds QUESTIONS; only a skip separates them — ADR-0004 §1.
  if (cap > 0) {
    const skipsCanOutrunTheCap = config.allow_skip && cap < count;
    rules.push({
      id: 'count',
      text: skipsCanOutrunTheCap
        ? `Up to ${count} questions — the round ends at ${cap} answers`
        : `${cap} question${cap === 1 ? '' : 's'}, one at a time`,
      emphasis: 'rule',
    });
  }
  // Says you needn't press Next, never that there is none — a Next button is on screen (ADR-0004 §1).
  if (count > 0 && blockQuestionTypes.every((t) => RAPID_FIRE_TAP_ANSWER_TYPES.has(t))) {
    rules.push({
      id: 'one_tap',
      text: 'One tap moves you on — you don’t need to press Next',
      emphasis: 'rule',
    });
  }
  if (config.allow_skip) {
    rules.push({
      id: 'skip',
      text: 'Stuck? Skip it. Skipping costs you nothing',
      emphasis: 'rule',
    });
  }
  // True of every block since the Undo was removed, so it no longer branches on the mix — ADR-0006.
  rules.push({
    id: 'no_back',
    text: 'You can’t go back or change an answer',
    emphasis: 'reassurance',
  });
  rules.push(
    {
      id: 'unfinished_ok',
      text: 'Don’t finish them all? That’s fine — the round just ends',
      emphasis: 'reassurance',
    },
    {
      id: 'clock_runs',
      text: 'The clock keeps running if you leave this screen',
      emphasis: 'reassurance',
    },
  );
  return rules;
}
